/**
 * Cart screen state — the priced cart for the chosen delivery address (``GET /cart``), quantity changes, coupons
 * (manual code + available-list picker + ``?coupon=`` hand-off), coin redemption, wallet as a payment source, the
 * payment methods available at the address, and placing the order (COD, mocked or real Razorpay). Moved verbatim
 * out of CartPage so the classic view and the Blinkit view share one implementation.
 *
 * Options (all used only by the Blinkit view; omitted = the original behaviour):
 *   waitForAuth — wait for the session before the signed-out → /login redirect.
 *   onPlaced(orderId) — where to go once the order exists (default: /orders/:id, replacing the cart).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveTenantSlug } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenant } from "../../../contexts/TenantContext";
import { openRazorpayCheckout } from "../../../lib/razorpay";
import { deliveryParams, deliveryPayload, lseOn } from "../../../lib/lse";
import { isMultiShopCart, multiShopPayload, orderGroupPath, payMethodsFor, shopIssues } from "../../../lib/multiShop";
import { isNetworkError, loadCartSnapshot, saveCartSnapshot } from "../../../lib/offlineCart";

export const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export function useCartController(opts = {}) {
    const { user, loading: authLoading } = useAuth();
    const { lang, tenant } = useTenant();
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [cart, setCart] = useState(null);
    const [code, setCode] = useState("");
    const [discount, setDiscount] = useState(0);
    const [coupon, setCoupon] = useState(null);
    const [busy, setBusy] = useState(false);
    const [addrs, setAddrs] = useState([]);
    const [picker, setPicker] = useState(false);
    const [showAddr, setShowAddr] = useState(false);
    const [selectedAddrId, setSelectedAddrId] = useState(null);
    const [payMethods, setPayMethods] = useState({ cod: true, online: true, razorpay_mock: true });
    const [payment, setPayment] = useState(null);
    const [placing, setPlacing] = useState(false);
    // Read only by the Blinkit view: what the place-order flow is doing right now, and why the last attempt failed.
    const [payPhase, setPayPhase] = useState(null);      // null | "placing" | "paying" | "confirming"
    const [lastError, setLastError] = useState(null);
    const goToOrder = (orderId) => (opts.onPlaced ? opts.onPlaced(orderId) : nav(`/orders/${orderId}`, { replace: true }));
    // Location Service Engine (tenants that switched it on only): the customer's delivery choice, and whether the
    // cart on screen is the device's saved copy because there is no connection. Both stay null / false otherwise.
    const [delivery, setDelivery] = useState(null);
    const [offline, setOffline] = useState(false);
    const offlineCart = lseOn(tenant, "offline_cart");
    // Multi-shop checkout (lib/multiShop.js) — only for a cart the server priced as spanning several shops. For
    // every other cart ``offered`` IS ``payMethods``, nothing is added to the payload and ``placed`` is ``goToOrder``.
    const requestRef = useRef(null);
    const multiShop = isMultiShopCart(cart);
    const offered = payMethodsFor(cart, payMethods);
    useEffect(() => {
        // Cash on delivery isn't offered for several shops unless the tenant allows it — move the selection off it.
        if (multiShop && !offered.cod) setPayment((curr) => (curr === "COD" ? (offered.online ? "ONLINE" : null) : curr));
    }, [multiShop, offered.cod, offered.online]);
    const placed = (data) => {
        const group = orderGroupPath(data);
        if (group) nav(group, { replace: true, state: { justPlaced: true } });
        else goToOrder(data.order_id || data.id);
    };

    // ---- Coupons: available-list picker (Phase 4) ----
    const [showCouponPicker, setShowCouponPicker] = useState(false);
    const [availCoupons, setAvailCoupons] = useState(null);
    const [couponPickerBusy, setCouponPickerBusy] = useState(false);

    // ---- Coin redemption ----
    const [coinsBalance, setCoinsBalance] = useState(0);
    const [coinsConfig, setCoinsConfig] = useState(null);
    const [coinsToRedeem, setCoinsToRedeem] = useState(0);

    // ---- Wallet as a payment source (iter65 — fixed in iter67) ----
    // The wallet is an ADDITIVE source: when checked we drain the
    // balance up to the grand total. If it covers the whole order the
    // COD/Online picker collapses; otherwise we still need a method
    // for the remaining amount.
    const [useWallet, setUseWallet] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);

    const load = async (addrIdOverride, opts = {}) => {
        if (!user) return nav("/login", { state: { from: "/cart" } });
        const addrId = addrIdOverride || selectedAddrId;
        // This screen shows the notice, so it's the one that removes lines the shop has switched off.
        const qs = new URLSearchParams({ remove_unavailable: "true" });
        if (addrId) qs.set("address_id", addrId);
        const redeem = opts.coinsRedeem != null ? opts.coinsRedeem : coinsToRedeem;
        if (redeem > 0) qs.set("coins_redeem", String(redeem));
        const couponCode = opts.couponCode != null ? opts.couponCode : coupon?.code;
        if (couponCode) qs.set("coupon_code", couponCode);
        deliveryParams(opts.delivery !== undefined ? opts.delivery : delivery, qs);
        let data;
        try {
            ({ data } = await api.get(`/cart?${qs.toString()}`));
        } catch (e) {
            const saved = offlineCart && isNetworkError(e) ? loadCartSnapshot(resolveTenantSlug(), user?.id) : null;
            if (!saved) throw e;
            setCart(saved);
            setOffline(true);
            return;
        }
        if (offlineCart) { saveCartSnapshot(resolveTenantSlug(), user?.id, data); setOffline(false); }
        setCart(data);
        // Sync local discount with the server-computed total so the line
        // item on the bill matches the deduction in "To Pay".
        const tot = data?.totals || {};
        const couponDisc = Number(tot.coupon_discount || 0);
        const coinsDisc = Number(tot.coins_discount || 0);
        setDiscount(round2(couponDisc + coinsDisc));

        // -----------------------------------------------------------
        // Auto-revoke flows. When the cart is edited and an applied
        // coupon / coin redemption no longer qualifies, the server
        // clears the persisted promo_code AND we clear local UI state
        // so the customer doesn't see a "ghost" discount.
        // -----------------------------------------------------------
        if (data?.cart_cleared) {
            // Server auto-cleared the cart because the underlying shop
            // is gone (admin hard-deleted it). Show the friendly
            // message and notify any subscribers (bottom-nav badge etc).
            toast.info(data.cart_cleared.message || "Your cart has been cleared.");
            window.dispatchEvent(new Event("cart:update"));
            setCoupon(null);
            setCode("");
            setCoinsToRedeem(0);
            setDiscount(0);
        }
        if (data?.coupon_revoked) {
            const { code: revokedCode, title, reason } = data.coupon_revoked;
            setCoupon(null);
            setCode("");
            toast.error(`Coupon ${title || revokedCode} removed — ${reason}`);
        }
        if (data?.items_removed) {
            // Server dropped lines the shop has switched off since they were added.
            toast.info(data.items_removed.message);
            window.dispatchEvent(new Event("cart:update"));
        }
        // Coin slider was set to N but the server clamped to 0 because the
        // cart no longer meets the minimum cap. Reset the slider so the UI
        // matches the bill and the customer can re-pick if they wish.
        if (redeem > 0 && Number(tot.coins_discount || 0) === 0) {
            setCoinsToRedeem(0);
            toast.info("Coin redemption removed — cart below minimum");
        }
    };
    const loadAddrs = async () => {
        const { data } = await api.get("/addresses");
        setAddrs(data || []);
        if (!selectedAddrId) {
            const def = (data || []).find((a) => a.is_default) || (data || [])[0];
            if (def) setSelectedAddrId(def.id);
        }
    };
    // ``waitForAuth`` (Blinkit view): don't decide "signed out → /login" before the session has loaded — a
    // refresh on /cart would otherwise bounce a signed-in customer to the login screen. Without the option the
    // effect runs exactly as it always has.
    const [loadError, setLoadError] = useState(false);     // Blinkit view: the cart request failed → offer a retry
    useEffect(() => {
        if (opts.waitForAuth && authLoading) return;
        const pending = load();
        if (opts.waitForAuth) Promise.resolve(pending).catch(() => setLoadError(true));
        const addrsLoad = loadAddrs();
        if (offlineCart) addrsLoad.catch(() => {});   // offline cart: a failed address fetch is expected while offline
        /* eslint-disable-next-line */
    }, [user, opts.waitForAuth ? authLoading : false]);
    const retryLoad = () => {
        setLoadError(false);
        Promise.resolve(load()).catch(() => setLoadError(true));
        loadAddrs().catch(() => {});
    };
    // Back online after showing the saved cart → fetch the live one again.
    useEffect(() => {
        if (!offline) return undefined;
        const onOnline = () => { Promise.resolve(load()).catch(() => {}); };
        window.addEventListener("online", onOnline);
        return () => window.removeEventListener("online", onOnline);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [offline]);
    const chooseDelivery = async (choice) => {
        setDelivery(choice);
        try { await load(selectedAddrId, { delivery: choice }); } catch { /* keeps the last priced cart */ }
    };

    // Auto-apply a ``?coupon=CODE`` query param that the Store Offers
    // page hands off via ``Use online``. We wait until the cart is
    // loaded so the apply-side validation has a restaurant_id to check
    // against, then strip the param so a manual refresh doesn't re-toast.
    useEffect(() => {
        const pre = searchParams.get("coupon");
        if (!pre || !cart || coupon || busy) return;
        setCode(pre.toUpperCase());
        // Defer to the next tick so ``setCode`` lands first.
        (async () => {
            try {
                const { data } = await api.post(`/coupons/apply?code=${encodeURIComponent(pre.toUpperCase())}`);
                setCoupon(data.coupon);
                await load(selectedAddrId, { couponCode: data.coupon?.code });
                toast.success(`Saved ₹${data.discount}`);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not apply that coupon");
            } finally {
                // Drop the query param so refresh / re-mount doesn't loop.
                searchParams.delete("coupon");
                setSearchParams(searchParams, { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart]);

    // Fetch coin balance + tenant config once. Hides the section silently
    // when coins are disabled at the tenant level.
    useEffect(() => {
        api.get("/customer/coins").then(({ data }) => {
            setCoinsBalance(Number(data?.balance) || 0);
            setCoinsConfig(data?.config || null);
        }).catch(() => {});
        // iter67 — wallet balance for the "Use my Wallet" toggle.
        api.get("/wallet").then(({ data }) => {
            setWalletBalance(Number(data?.wallet?.balance) || 0);
        }).catch(() => {});
    }, []);

    // Recompute totals when the customer changes their coin redemption
    // (delivery address and coupon already trigger a reload via their own
    // handlers).
    useEffect(() => {
        if (!cart) return;
        load(selectedAddrId, { coinsRedeem: coinsToRedeem });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coinsToRedeem]);

    // Re-compute cart totals whenever the user switches delivery address —
    // distance-based delivery fee tracks the new address in real time.
    useEffect(() => {
        if (!selectedAddrId || !cart) return;
        load(selectedAddrId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAddrId]);

    const selectedAddr = useMemo(() => addrs.find((a) => a.id === selectedAddrId), [addrs, selectedAddrId]);

    // Whenever the selected address changes, ask which payment modes are available there
    useEffect(() => {
        if (!selectedAddr) return;
        const params = new URLSearchParams();
        if (selectedAddr.lat) { params.set("lat", selectedAddr.lat); params.set("lng", selectedAddr.lng); }
        if (selectedAddr.pincode) params.set("pincode", selectedAddr.pincode);
        api.get(`/payment-methods?${params.toString()}`).then(({ data }) => {
            setPayMethods(data);
            // Default to Online if available, else COD. Snap when current selection becomes unavailable.
            setPayment((curr) => {
                if (!curr) return data.online ? "ONLINE" : (data.cod ? "COD" : null);
                if (curr === "ONLINE" && !data.online) return data.cod ? "COD" : null;
                if (curr === "COD" && !data.cod) return data.online ? "ONLINE" : null;
                return curr;
            });
        }).catch(() => {});
    }, [selectedAddr]); // eslint-disable-line

    // Offline (Location Service Engine offline cart): the saved cart is view-only — say so instead of failing.
    const OFFLINE_MSG = "You're offline — your cart is saved. Changes can be made once you're back online.";
    const change = async (item, delta) => {
        if (offline) { toast.info(OFFLINE_MSG); return; }
        const newQty = item.quantity + delta;
        try {
            const aid = selectedAddrId ? `&address_id=${selectedAddrId}` : "";
            await api.patch(`/cart/items/${item.product_id}?quantity=${newQty}${item.variant_id ? `&variant_id=${item.variant_id}` : ""}${aid}`);
            await load();
            window.dispatchEvent(new Event("cart:update"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
            // e.g. the item was switched off while this page was open — reloading removes it.
            if (e?.response?.status === 409) load().catch(() => {});
        }
    };

    const apply = async () => {
        if (!code) return;
        if (offline) { toast.info(OFFLINE_MSG); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/coupons/apply?code=${encodeURIComponent(code.toUpperCase())}`);
            setCoupon(data.coupon);
            // Refetch cart with new coupon so server-side totals reflect
            // discount + (any active) coin redemption together.
            await load(selectedAddrId, { couponCode: data.coupon?.code });
            toast.success(`Saved ₹${data.discount}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
        finally { setBusy(false); }
    };
    const removeCoupon = async () => {
        setCoupon(null);
        setCode("");
        // iter109 — clear the persisted promo_code on the cart so the
        // very next GET /cart doesn't silently rehydrate it (previously
        // `effective_coupon = coupon_code or cart.promo_code` fell back
        // to the persisted value when the caller passed nothing).
        try {
            await api.post("/coupons/remove");
        } catch { /* soft-fail — backend will still auto-revoke on next load */ }
        await load(selectedAddrId, { couponCode: "" });
    };

    const openCouponPicker = async () => {
        setShowCouponPicker(true);
        if (availCoupons !== null) return;
        setCouponPickerBusy(true);
        try {
            const rid = cart?.restaurant_id;
            const qs = new URLSearchParams();
            if (rid) qs.set("restaurant_id", rid);
            // Location Service Engine: area-targeted offers are listed only where they apply.
            if (cart?.lse && selectedAddr?.pincode) qs.set("pincode", selectedAddr.pincode);
            if (cart?.lse && selectedAddr?.lat != null) { qs.set("lat", selectedAddr.lat); qs.set("lng", selectedAddr.lng); }
            const url = qs.toString() ? `/coupons/available?${qs.toString()}` : "/coupons/available";
            const { data } = await api.get(url);
            setAvailCoupons(Array.isArray(data) ? data : []);
        } catch {
            setAvailCoupons([]);
        } finally {
            setCouponPickerBusy(false);
        }
    };

    const applyCouponFromPicker = async (couponCode) => {
        if (offline) { toast.info(OFFLINE_MSG); return; }
        setShowCouponPicker(false);
        setCode(String(couponCode || "").toUpperCase());
        // Reuse the validated apply endpoint so we get the same
        // server-side checks (zone gating, shop scope, minimum cart, …).
        setBusy(true);
        try {
            const { data } = await api.post(`/coupons/apply?code=${encodeURIComponent(String(couponCode).toUpperCase())}`);
            setCoupon(data.coupon);
            await load(selectedAddrId, { couponCode: data.coupon?.code });
            toast.success(`Saved ₹${data.discount}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not apply coupon");
        } finally { setBusy(false); }
    };

    const placeOrder = async () => {
        if (!selectedAddr) { toast.error("Please select a delivery address"); return; }
        if (offline) { toast.error("You're offline — your cart is saved. Connect to place the order."); return; }
        const shopIssue = shopIssues(cart)[0];      // a shop in a multi-shop cart that can't take the order right now
        if (shopIssue) { toast.error(shopIssue.message); return; }
        setPlacing(true);
        setLastError(null);
        setPayPhase("placing");
        try {
            const payload = {
                delivery_address: {
                    label: selectedAddr.label, line1: selectedAddr.line1, line2: selectedAddr.line2,
                    city: selectedAddr.city, state: selectedAddr.state, pincode: selectedAddr.pincode,
                    lat: selectedAddr.lat, lng: selectedAddr.lng,
                },
                // Backend's payment-status logic keys off the exact string
                // "RAZORPAY" (mock or real, depending on the tenant's
                // Razorpay config) — "ONLINE" matched neither branch and
                // silently left online orders stuck at payment_status
                // PENDING forever.
                payment_method: payment === "ONLINE" ? "RAZORPAY" : payment,
                coupon_code: coupon?.code,
                coins_redeem: coinsToRedeem > 0 ? coinsToRedeem : undefined,
                use_wallet: useWallet,
                ...deliveryPayload(delivery),
                ...multiShopPayload(requestRef, cart),
            };

            if (payment === "ONLINE" && !payMethods.razorpay_mock) {
                // Real Razorpay flow: server creates the order first, we
                // open Checkout.js against it, then forward the payment
                // proof so the server can verify it before trusting "paid".
                const { data: rzpOrder } = await api.post("/payments/razorpay/create-order", payload);
                setPayPhase("paying");
                const proof = await openRazorpayCheckout({
                    keyId: rzpOrder.key_id,
                    orderId: rzpOrder.razorpay_order_id,
                    amount: rzpOrder.amount,
                    name: tenant?.name,
                    description: "Order payment",
                    themeColor: tenant?.branding?.custom_primary_hex,
                    prefill: { name: user?.first_name, contact: user?.mobile_number },
                });
                setPayPhase("confirming");
                const { data } = await api.post("/orders/checkout", { ...payload, ...proof });
                window.dispatchEvent(new Event("cart:update"));
                toast.success("Payment successful — order placed!");
                placed(data);
                return;
            }

            const { data } = await api.post("/orders/checkout", payload);
            window.dispatchEvent(new Event("cart:update"));
            if (payment === "ONLINE" && payMethods.razorpay_mock) {
                toast.success("Mock payment captured");
            }
            toast.success("Order placed!");
            placed(data);
        } catch (e) {
            // A dismissed/failed Razorpay sheet rejects with a plain
            // Error (no ``response``) — no order was created in that
            // case, so skip the orphan-cart recovery path below.
            const msg = e?.response?.data?.detail || e?.message || "Failed to place order";
            toast.error(msg);
            setLastError(typeof msg === "string" ? msg : "Failed to place order");
            if (multiShop && e?.response?.status === 409) {
                // The server refused or changed the cart (an item sold out, a shop left it): show it as it is now.
                window.dispatchEvent(new Event("cart:update"));
                try { await load(); } catch { /* keep what is on screen */ }
            }
            if (/shop is no longer available|has been removed by the store owner|not available on your storefront|removed from your cart/i.test(msg)) {
                window.dispatchEvent(new Event("cart:update"));
                try { await load(); } catch { /* ignore — cart will be empty */ }
            }
        } finally { setPlacing(false); setPayPhase(null); }
    };

    return {
        user, lang, tenant, cart, code, setCode, discount, coupon, busy, addrs, picker, setPicker, showAddr, setShowAddr,
        selectedAddrId, setSelectedAddrId, payMethods: offered, payment, setPayment, placing, payPhase, lastError, setLastError,
        showCouponPicker, setShowCouponPicker, availCoupons, couponPickerBusy, coinsBalance, coinsConfig, coinsToRedeem,
        setCoinsToRedeem, useWallet, setUseWallet, walletBalance, load, loadAddrs, loadError, retryLoad, selectedAddr, change, apply,
        removeCoupon, openCouponPicker, applyCouponFromPicker, placeOrder,
        // Location Service Engine (present only for tenants with it on)
        lse: cart?.lse || null, delivery, chooseDelivery, offline,
    };
}
